// ===========================================================================
// Membership Analytics (Module J) — capa de datos.
//
// Combina datos reales de muestra (DataDashCEO_v2) con fake data determinístico
// (fakeData.js) para lo que aún no existe en backend (tendencia mensual,
// churn por centro, PMPM). Cada loader tiene try/catch con fallback.
//
// Migración futura: reemplazar los dynamic imports por llamadas API
// (ver endpoints anotados en fakeData.js).
// ===========================================================================

import {
  FAKE_MEMBERSHIP_TREND,
  FAKE_AVG_REVENUE_PMPM,
  FAKE_PLAN_MIX,
  getCenterChurnProfile,
} from "./fakeData";

/** Total de miembros fallback si el JSON real no carga. */
const FALLBACK_TOTAL_MEMBERS = 10462;

/** Overview poblacional real (totalMembers, newMembersCount). */
async function loadPopulationOverview() {
  try {
    const mod = await import(
      "../../../pages/Main/Temp/DataDashCEO_v2/population/overview.json"
    );
    return mod.default || mod;
  } catch (e) {
    return null;
  }
}

/** Performance por centro real (totalPatients, newMembers por centro). */
async function loadCentersPerformance() {
  try {
    const mod = await import(
      "../../../pages/Main/Temp/DataDashCEO_v2/centers/performance.json"
    );
    const data = mod.default || mod;
    return Array.isArray(data.centers) ? data.centers : [];
  } catch (e) {
    return [];
  }
}

/** Extrae el tier del nombre del plan ("myBlue Bronze 2129" → "Bronze"). */
const planTier = (plan = "") => {
  const m = plan.match(/Bronze|Silver|Gold|Platinum|Catastrophic/i);
  return m ? m[0].charAt(0).toUpperCase() + m[0].slice(1).toLowerCase() : "Other";
};

/**
 * Plan mix real agregado desde el roster de nuevos miembros.
 * Fallback: FAKE_PLAN_MIX.
 */
async function loadPlanMix() {
  try {
    const mod = await import(
      "../../../pages/Main/Temp/DataDashCEO_v2/population/new_members.json"
    );
    const data = mod.default || mod;
    const members = Array.isArray(data.members) ? data.members : [];
    if (!members.length) return { plans: FAKE_PLAN_MIX, source: "fake" };

    const counts = new Map();
    members.forEach((m) => {
      const plan = m.plan || "Unknown";
      counts.set(plan, (counts.get(plan) || 0) + 1);
    });
    const total = members.length;
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 6).map(([plan, n]) => ({
      plan,
      members: n,
      pct: (n / total) * 100,
    }));
    const restCount = sorted.slice(6).reduce((acc, [, n]) => acc + n, 0);
    if (restCount > 0) {
      top.push({ plan: "Other plans", members: restCount, pct: (restCount / total) * 100 });
    }
    return { plans: top, source: "sample" };
  } catch (e) {
    return { plans: FAKE_PLAN_MIX, source: "fake" };
  }
}

/** Agrega el mix por tier (Bronze/Silver/Gold...) a partir del plan mix. */
const buildTierMix = (plans) => {
  const tiers = new Map();
  plans.forEach((p) => {
    const tier = planTier(p.plan);
    tiers.set(tier, (tiers.get(tier) || 0) + p.members);
  });
  const total = plans.reduce((acc, p) => acc + p.members, 0) || 1;
  return [...tiers.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([tier, n]) => ({ tier, members: n, pct: (n / total) * 100 }));
};

/**
 * Carga y ensambla todo el dataset del módulo.
 *
 * @returns {Promise<Object>} {
 *   signals: { totalMembers, netMoM, churnRate, avgRevenuePmpm, newMembersYtd },
 *   trend: [{ month, members, adds, drops, net }],
 *   centers: [{ centerName, members, newMembers, netMoM, churnRate, avgPmpm, sharePct }],
 *   planMix: { plans, tiers }
 * }
 */
export async function getMembershipAnalytics() {
  const [overview, centersRaw, planMixRaw] = await Promise.all([
    loadPopulationOverview(),
    loadCentersPerformance(),
    loadPlanMix(),
  ]);

  const totalMembers = overview?.totalMembers ?? FALLBACK_TOTAL_MEMBERS;

  // Tendencia: serie fake anclada al total real en el último mes.
  const trend = FAKE_MEMBERSHIP_TREND.map((m) => ({ ...m, net: m.adds - m.drops }));
  const last = trend[trend.length - 1];
  const prev = trend[trend.length - 2];
  last.members = totalMembers;
  last.net = last.members - prev.members;

  const churnRate = (last.drops / prev.members) * 100;

  // Centros: datos reales + perfil churn determinístico.
  const networkTotal =
    centersRaw.reduce((acc, c) => acc + (c.totalPatients || 0), 0) || totalMembers;
  const centers = centersRaw
    .map((c) => {
      const profile = getCenterChurnProfile(c.centerName, c.totalPatients);
      return {
        centerName: c.centerName,
        members: c.totalPatients || 0,
        newMembers: c.newMembers || 0,
        netMoM: profile.netMoM,
        churnRate: profile.churnRate,
        avgPmpm: profile.avgPmpm,
        sharePct: ((c.totalPatients || 0) / networkTotal) * 100,
      };
    })
    .sort((a, b) => b.members - a.members);

  return {
    signals: {
      totalMembers,
      netMoM: last.net,
      churnRate,
      avgRevenuePmpm: FAKE_AVG_REVENUE_PMPM,
      newMembersYtd: overview?.newMembersCount ?? 923,
    },
    trend,
    centers,
    planMix: { plans: planMixRaw.plans, tiers: buildTierMix(planMixRaw.plans) },
  };
}
